"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import type { Range } from "@tiptap/core";
import { setOpenMediaLinkModal, type MediaLinkType } from "@/lib/media-link-modal-helper";

interface MediaLinkModalContextValue {
  isOpen: boolean;
  type: MediaLinkType | null;
  editor: Editor | null;
  range: Range | null;
  position: { top: number; left: number } | null;
  openModal: (editor: Editor, range: Range, type: MediaLinkType, position?: { top: number; left: number }) => void;
  closeModal: () => void;
}

const MediaLinkModalContext = createContext<MediaLinkModalContextValue | undefined>(undefined);

export function MediaLinkModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<MediaLinkType | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [range, setRange] = useState<Range | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const openModal = useCallback(
    (editorInstance: Editor, rangeInstance: Range, mediaType: MediaLinkType, pos?: { top: number; left: number }) => {
      setEditor(editorInstance);
      setRange(rangeInstance);
      setType(mediaType);
      setPosition(pos || null);
      setIsOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setEditor(null);
      setRange(null);
      setType(null);
      setPosition(null);
    }, 100);
  }, []);

  useEffect(() => {
    setOpenMediaLinkModal(openModal);
    return () => setOpenMediaLinkModal(null);
  }, [openModal]);

  return (
    <MediaLinkModalContext.Provider value={{ isOpen, type, editor, range, position, openModal, closeModal }}>
      {children}
    </MediaLinkModalContext.Provider>
  );
}

export function useMediaLinkModalContext() {
  const context = useContext(MediaLinkModalContext);
  if (!context) throw new Error("useMediaLinkModalContext must be used within MediaLinkModalProvider");
  return context;
}
